import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import moment from 'moment';
import toast from 'react-hot-toast';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { FiChevronLeft, FiHelpCircle, FiZap, FiCheckCircle, FiUser, FiDownload } from 'react-icons/fi';
import { Error404 } from '@/components/custom/error';
import { PATHS } from '@/routes/paths';
import { AvatarWrapper } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import usePermission from '@/hooks/usePermission';
import { useAuthorityLevels } from '@/hooks/useAuthorityLevel';
import { useSession } from '@/hooks/useSession';
import { downloadReportFile } from '@/utils/downloadReportFile';
import { UsageLineChart } from './charts/UsageLineChart';
import { ChallengesTable } from './ChallengesTable';
import { SectionState } from './SectionState';
import { useReportSection } from './useReportSection';
import { buildDateQuery, buildUsageChartData, mapChallenges, CHALLENGES_PER_PAGE } from './reportApi';
import { formatInteger } from './formatters';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import usePlayfulNav from '@/hooks/usePlayfulNav';
import { Header } from '@/components/custom/header/header';
import { BottomBarProfessor } from '@/components/custom/bottom-bar-professor';
import { DashboardContent } from '@/layouts/dashboard';
import { PlayfulCard, PlayfulCardButton } from '@/components/custom/playful-card';

const BASE_PATH = '/reports/live_challenges';

const PLAYFUL_KPI_STYLES = {
  created: { icon: FiZap, iconBg: 'bg-[#E0FBF9]', iconColor: 'text-[#12B8AE]' },
  finished: { icon: FiCheckCircle, iconBg: 'bg-[#FFEEDD]', iconColor: 'text-[#FF8A3D]' },
  reached: { icon: FiUser, iconBg: 'bg-[#EDE7FA]', iconColor: 'text-[#8B5CF6]' }
};

export function LiveChallengeTeacherReport() {
  const navigate = useNavigate();
  const { teacherId } = useParams();
  const client = useClient();
  const { session } = useSession();
  const authorityLevels = useAuthorityLevels();
  const playfulTeacher = usePlayfulTeacher();
  const playfulNav = usePlayfulNav();

  const hasPermission = usePermission('Visualizar Relatório de Desafio Ao Vivo', 'Relatórios');
  const isAdmin = authorityLevels.includes('admin');
  const isOwnProfile = String(session?.user?.id) === String(teacherId);
  const canAccess = hasPermission && (isAdmin || isOwnProfile);

  // A navegação vinda da listagem de professores (ou de volta pra ela) é
  // client-side (pushState) e o navegador não reseta o scroll sozinho nessa
  // transição — só num reload de página. `behavior: 'instant'` é necessário
  // pq até a atribuição direta em scrollTop respeita o `scroll-behavior:
  // smooth` global (App.css); só passando 'instant' explicitamente essa
  // chamada ignora o CSS e abre a tela já no topo, sem precisar mexer em
  // nada global.
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [teacherId]);

  // Datas ficam na URL (?start_date=&end_date=) em vez de em estado local
  // pra sobreviver à navegação entre este relatório e o relatório geral de
  // Desafio ao Vivo (REPORTS.LIVE_CHALLENGE), nos dois sentidos — ver
  // index.jsx e o botão "Voltar" abaixo.
  const [searchParams, setSearchParams] = useSearchParams();
  const startDate = searchParams.get('start_date') || moment().subtract(14, 'days').format('YYYY-MM-DD');
  const endDate = searchParams.get('end_date') || moment().format('YYYY-MM-DD');

  const setStartDate = value => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('start_date', value);
        return next;
      },
      { replace: true }
    );
  };

  const setEndDate = value => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('end_date', value);
        return next;
      },
      { replace: true }
    );
  };

  const [challengeSearch, setChallengeSearch] = useState('');
  const [debouncedChallengeSearch, setDebouncedChallengeSearch] = useState('');
  const [teacherProfile, setTeacherProfile] = useState(null);

  // Lista de desafios usa infinite scroll (mesmo padrão de RegistrationsTable/
  // LiveClassesTable em GeneralRegistrationReport), não paginação numerada.
  const [challenges, setChallenges] = useState([]);
  const [challengePage, setChallengePage] = useState(1);
  const [challengesHasMore, setChallengesHasMore] = useState(true);
  const [challengesInitialLoading, setChallengesInitialLoading] = useState(true);
  const [challengesNotFound, setChallengesNotFound] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedChallengeSearch(challengeSearch.trim()), 300);
    return () => clearTimeout(timeout);
  }, [challengeSearch]);

  const loadChallenges = async (page, replace) => {
    if (!canAccess) return;

    const params = new URLSearchParams({ ...buildDateQuery(startDate, endDate), 'page[number]': page, 'page[size]': CHALLENGES_PER_PAGE });
    if (debouncedChallengeSearch) params.set('q', debouncedChallengeSearch);

    const { data, error, meta } = await client.fetch(`${BASE_PATH}/teachers/${teacherId}/challenges?${params}`);

    if (error) {
      if (String(error?.status) === '404') {
        setChallengesNotFound(true);
      } else {
        toast.error('Erro ao buscar os desafios.');
      }
    } else {
      const mapped = mapChallenges(data.challenges);
      setChallenges(prev => (replace ? mapped : [...prev, ...mapped]));
      const pagination = meta?.pagination;
      setChallengesHasMore(pagination ? pagination.current_page < pagination.total_pages : false);
    }

    if (replace) setChallengesInitialLoading(false);
  };

  useEffect(() => {
    setChallenges([]);
    setChallengePage(1);
    setChallengesHasMore(true);
    setChallengesInitialLoading(true);
    loadChallenges(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherId, startDate, endDate, debouncedChallengeSearch]);

  useEffect(() => {
    if (challengePage > 1) {
      loadChallenges(challengePage, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengePage]);

  useEffect(() => {
    if (!canAccess || !teacherId) return undefined;
    let active = true;
    client.fetch(`users/${teacherId}?with_classrooms=true`).then(({ data, error }) => {
      if (!active || error) return;
      setTeacherProfile(data);
    });
    return () => {
      active = false;
    };
  }, [teacherId, canAccess]);

  const dateQuery = buildDateQuery(startDate, endDate);

  const kpisSection = useReportSection(() => (canAccess ? client.fetch(`${BASE_PATH}/teachers/${teacherId}/kpis?${new URLSearchParams(dateQuery)}`) : Promise.resolve({ data: null, error: false })), [teacherId, startDate, endDate, canAccess]);
  const usageSection = useReportSection(() => (canAccess ? client.fetch(`${BASE_PATH}/teachers/${teacherId}/usage_chart?${new URLSearchParams(dateQuery)}`) : Promise.resolve({ data: null, error: false })), [teacherId, startDate, endDate, canAccess]);

  const notFound = challengesNotFound || [kpisSection.error, usageSection.error].some(error => String(error?.status) === '404');

  const kpis = [
    { id: 'created', title: 'Desafios criados', value: kpisSection.data?.['created-challenges'], variation: 'report-kpi-card--lightning' },
    { id: 'finished', title: 'Ativações finalizadas', value: kpisSection.data?.['finished-activations'], variation: 'report-kpi-card--lightning' },
    { id: 'reached', title: 'Alunos alcançados', value: kpisSection.data?.['reached-students'], variation: 'report-kpi-card--users', help: 'Alunos alcançados soma participantes de todas as escolas, mesmo fora da escola do docente.' }
  ];

  const usage = useMemo(() => (usageSection.data ? buildUsageChartData(usageSection.data['chart-data'], { startDate, endDate }) : null), [usageSection.data, startDate, endDate]);

  // `?with_classrooms=true` (mesmo parâmetro usado em CreateOrUpdateParticipant
  // para editar o professor) traz `classrooms`, cada uma com `institution`
  // (com `regional` aninhado), `course_title` e `title` — não existe endpoint
  // de "perfil do professor" nesta feature, então essa é a única fonte pra
  // GRE/instituição/turma. Mostramos só a primeira turma da lista; o "+N"
  // indica quantas outras turmas o professor tem além dessa.
  const classrooms = teacherProfile?.classrooms ?? [];
  const firstClassroom = classrooms[0];
  const grade = firstClassroom ? [firstClassroom.course_title, firstClassroom.title].filter(Boolean).join(' - ') : null;
  const location = firstClassroom?.institution ? [firstClassroom.institution.regional?.name, firstClassroom.institution.name].filter(Boolean).join(' - ') : null;

  // Tooltip nativo (atributo title) do "+N": uma turma por parágrafo, cada
  // uma em duas linhas (curso/turma + regional/instituição), igual ao card
  // principal — \n\n vira quebra de parágrafo, \n vira quebra de linha.
  const extraClassrooms = classrooms.slice(1);
  const extraClassroomsTooltip = extraClassrooms
    .map(classroom => {
      const gradeLine = [classroom.course_title, classroom.title].filter(Boolean).join(' - ');
      const locationLine = classroom.institution ? [classroom.institution.regional?.name, classroom.institution.name].filter(Boolean).join(' - ') : null;
      return [gradeLine, locationLine].filter(Boolean).join('\n');
    })
    .join('\n\n');

  const handleExport = async () => {
    try {
      await downloadReportFile(`${BASE_PATH}/teachers/${teacherId}/challenges.xlsx`, dateQuery);
    } catch (error) {
      toast.error('Erro ao baixar o relatório');
    }
  };

  if (!canAccess || notFound) {
    return <Error404 />;
  }

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      { route: PATHS.LIVE_CHALLENGE.ADMIN_LIST, name: 'Desafio ao Vivo' },
      { route: '#', name: 'Meu histórico de ativações', isActive: true }
    ]
  };

  const dateFilter = (
    <div className="report__filter">
      <div className="report-form__datepicker">
        <input
          className="report-form__control"
          id="start-date"
          name="start-date"
          type="date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          title="Data de início"
          aria-label="Data de início"
        />
        <input
          className="report-form__control"
          id="end-date"
          name="end-date"
          type="date"
          value={endDate}
          min={startDate}
          onChange={e => setEndDate(e.target.value)}
          title="Data de fim"
          aria-label="Data de fim"
        />
      </div>
    </div>
  );

  const reportBody = (
    <>
      {isAdmin && (
        <button
          type="button"
          className="btn btn--none flex w-fit items-center gap-1 text-sm font-semibold text-primary"
          onClick={() => navigate(`${PATHS.REPORTS.LIVE_CHALLENGE}?${searchParams.toString()}`)}
        >
          <FiChevronLeft />
          Voltar
        </button>
      )}

      {!playfulTeacher && dateFilter}

      {playfulTeacher ? (
        <>
          <PlayfulCard className="flex flex-col gap-4 p-6">
            <h3 className="mb-0 text-base font-extrabold text-primary-dark">Minhas Ativações</h3>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {kpis.map(kpi => {
                const style = PLAYFUL_KPI_STYLES[kpi.id];
                const Icon = style.icon;

                return (
                  <div
                    key={kpi.id}
                    className="flex items-center gap-3 rounded-2xl border border-neutral-lightest bg-white p-4 shadow-sm"
                  >
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${style.iconBg}`}>
                      <Icon className={`h-5 w-5 ${style.iconColor}`} />
                    </span>
                    <div className="min-w-0">
                      <p className="mb-0 truncate text-xs font-bold uppercase tracking-wide text-neutral-500">{kpi.title}</p>
                      <p className="mb-0 font-extrabold text-neutral-800">{formatInteger(kpi.value)}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="mb-0 self-end rounded-sm border border-neutral-lightest bg-neutral-bright px-2 py-1 text-right text-xs text-neutral-darker">Alunos alcançados soma participantes de todas as escolas, mesmo fora da escola do docente.</p>
          </PlayfulCard>

          {dateFilter}
        </>
      ) : (
        <div className="report-widget gap-6">
          <div className="flex items-center gap-4 rounded-xl bg-gradient-to-r from-primary-dark to-primary p-6">
            <AvatarWrapper
              size="lg"
              src={teacherProfile?.image?.url}
              text={teacherProfile?.name}
              className="size-16 border-2 border-white sm:size-32 [&_span]:text-lg sm:[&_span]:text-3xl"
            />
            <div>
              <h2 className="text-lg font-bold text-white">{teacherProfile?.name || 'Professor(a)'}</h2>
              {grade && <p className="text-sm text-white/80">{grade}</p>}
              {location && (
                <p className="flex flex-wrap items-center gap-2 text-sm text-white/80">
                  {location}
                  {extraClassrooms.length > 0 && (
                    <span
                      className="badge badge--more"
                      title={extraClassroomsTooltip}
                    >
                      {`+${extraClassrooms.length}`}
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>

          <div className="report-widget__header">
            <h3 className="report-widget__title">Visão geral</h3>
          </div>

          <SectionState
            loading={kpisSection.loading}
            error={kpisSection.error}
            onRetry={kpisSection.reload}
            errorTitle="Não foi possível carregar os indicadores"
          >
            <div className="report-kpi-card-wrapper">
              {kpis.map(kpi => (
                <div
                  className={`report-kpi-card ${kpi.variation}`}
                  key={kpi.id}
                >
                  {kpi.help && (
                    <>
                      <FiHelpCircle
                        className="report-kpi-card__help-icon"
                        data-tooltip-id={`kpi-help-${kpi.id}`}
                        data-tooltip-content={kpi.help}
                      />
                      <ReactTooltip
                        id={`kpi-help-${kpi.id}`}
                        className="tooltip"
                      />
                    </>
                  )}
                  <h3 className="report-kpi-card__title">{kpi.title}</h3>
                  <p className="report-kpi-card__value">{formatInteger(kpi.value)}</p>
                </div>
              ))}
            </div>
          </SectionState>
        </div>
      )}

      <div className="report-widget">
        <div className="report-widget__header report-widget__header--column">
          <h3 className="report-widget__title">Uso ao longo do tempo</h3>
          <p className="report-widget__subtitle">Quantidade de desafios criados ao longo do período selecionado</p>
        </div>
        <div className="report-widget__body">
          <SectionState
            loading={usageSection.loading}
            error={usageSection.error}
            onRetry={usageSection.reload}
            errorTitle="Não foi possível carregar o gráfico de uso"
          >
            <UsageLineChart
              categories={usage?.categories}
              series={usage?.series}
            />
          </SectionState>
        </div>
      </div>

      <div className="report-widget">
        <div className="report-widget__header">
          <h3 className="report-widget__title">Lista de Desafios ao Vivo</h3>
          {playfulTeacher ? (
            <PlayfulCardButton
              className="h-9 w-fit px-3"
              onClick={handleExport}
              variant="outline"
              icon={FiDownload}
            >
              Exportar relatório
            </PlayfulCardButton>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={handleExport}
            >
              Exportar relatório
            </Button>
          )}
        </div>
        <div className="report-widget__body">
          <ChallengesTable
            challenges={challenges}
            search={challengeSearch}
            onSearchChange={setChallengeSearch}
            hasMore={challengesHasMore}
            onLoadMore={() => setChallengePage(page => page + 1)}
            initialLoading={challengesInitialLoading}
          />
        </div>
      </div>
    </>
  );

  // No tema lúdico o professor navega pelo header + bottom bar do layout
  // principal, mas /relatorio/* mora no adminLayout, que não monta nenhum dos
  // dois (page-layouts.jsx monta ambos só sob `isMainLayout && session`) — por
  // isso a própria página os renderiza, no mesmo par Header + DashboardContent
  // de essay-send/PlayfulTeacher. O report__header (faixa roxa com título e
  // botão de voltar) é a moldura dos relatórios corporativos e sai de cena:
  // aqui quem dá título e caminho de volta é o breadcrumb.
  if (playfulTeacher) {
    return (
      <>
        <BottomBarProfessor />
        <Header playfulNav={playfulNav} />

        <DashboardContent
          variant="plain"
          breadcrumbs={breadcrumbs}
        >
          <div className="flex flex-col gap-8">{reportBody}</div>
        </DashboardContent>
      </>
    );
  }

  return (
    <div className="report">
      <header className="report__header">
        <h1 className="report__title">
          <button
            className="card__icon-wrapper"
            aria-label="Voltar"
            title="Voltar"
            onClick={() => navigate(isAdmin ? '/metricas/institucional' : PATHS.LIVE_CHALLENGE.ADMIN_LIST)}
          >
            <FiChevronLeft className="card__icon" />
          </button>
          Desafio Ao Vivo
        </h1>
        <p className="report__description">Acompanhe a participação dos estudantes nas disputas em tempo real</p>
      </header>

      <main className="report__body">{reportBody}</main>
    </div>
  );
}
